import { ButtonLoading } from "@/components/button-loading";
import { LicenseCountSelector } from "@/components/setup/license-count-selector";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { useToast, useToastError } from "@/components/ui/use-toast";
import { useUser } from "@/contexts/user-provider";
import { useUsers } from "@/contexts/users-provider";
import { useApi } from "@/hooks/useApi";
import { useIdempotencyKey } from "@/hooks/use-idempotency-key";
import { useLicenseErrorToast } from "@/hooks/use-license-error-toast";
import { useTranslation } from "@/hooks/useTranslation";
import { formatCurrency, formatDateNumeric } from "@/lib/format-amount";
import { toHumanReadableAmount } from "@/lib/string";
import { LicenseChangeSummary } from "@/components/billing/license-change-summary";
import { buildLicenseUpdateToast } from "@/components/billing/license-update-toast";
import {
  INVOICE_DAYS_UNTIL_DUE,
  LicenseFrequencyDTO,
  LicensePreviewDTO,
  LicenseQuantitiesUpdateResultDTO,
  TeamBillingPortalDTO,
  TeamInvitationDTO,
  TeamLicenseInventoryDTO,
  TeamUserDTO,
} from "@shaarp/common";
import { AlertCircleIcon, InfoIcon, Loader2Icon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Seats are sold in euros only; the preview carries the currency once it has
 * answered, this covers the moment before.
 */
const DEFAULT_CURRENCY = "eur";

type AssignedItem =
  | { kind: "user"; id: string; label: string; isSelf: boolean }
  | { kind: "invitation"; id: string; label: string };

type DialogLicenseManagementProps = {
  frequency: "monthly" | "annual";
  inventory: TeamLicenseInventoryDTO;
  users: TeamUserDTO[];
  invitations: TeamInvitationDTO[];
  onClose: () => void;
  onConfirm: (updatedInventory: TeamLicenseInventoryDTO) => void;
  onRequestCancel: () => void;
}

/**
 * Dialog for managing license quantities for a given frequency.
 * Debounces calls to preview API, shows proration details, and handles user unassign when reducing.
 */
export const DialogLicenseManagement = ({
  frequency,
  inventory,
  users,
  invitations,
  onClose,
  onConfirm,
  onRequestCancel,
}: DialogLicenseManagementProps) => {
  const { t, i18n } = useTranslation();
  const api = useApi();
  const { toast } = useToast();
  const toastError = useToastError();
  const showLicenseError = useLicenseErrorToast();
  const idempotencyKey = useIdempotencyKey();
  const { apiFetchUsers } = useUsers();
  const { teamUser } = useUser();

  const freqData: LicenseFrequencyDTO =
    frequency === "monthly" ? inventory.monthly : inventory.annual;

  const hasPaymentMethod = freqData.hasPaymentMethod ?? inventory.hasPaymentMethod;
  const paidByInvoice = freqData.paidByInvoice ?? inventory.paidByInvoice;

  const priceInterval = frequency === "monthly" ? "month" : "year";

  const proPrice = inventory.prices.find(
    (p) => p.licenceType === "professional" && p.interval === priceInterval
  );

  // When a scheduled decrease is pending, default to the scheduled quantities —
  // that's the user's last decision and what they'll be billed for at renewal.
  // Re-opening the dialog and confirming should be a no-op, not an accidental
  // revert of the pending change.
  const [proQty, setProQty] = useState(
    freqData.scheduledPro ?? freqData.pro.purchased
  );

  const [preview, setPreview] = useState<LicensePreviewDTO | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Selection is keyed by `${kind}:${id}` so users and invitations share one
  // state map without clashing on UUIDs. Pre-select users who already have a
  // scheduled unassignment for this frequency's price IDs — they were the
  // manager's previous pick. Pending invitations get cancelled immediately on
  // confirm so they're never persisted as a "pre-selected" state.
  const [selectedItems, setSelectedItems] = useState<Set<string>>(
    () =>
      new Set(
        users
          .filter(
            (u) =>
              u.scheduledUnassignAt &&
              u.priceId === proPrice?.id
          )
          .map((u) => `user:${u.id}`)
      )
  );

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Monotonic id so a slow in-flight preview can't overwrite a newer one.
  const previewSeqRef = useRef(0);

  const formatDate = (date: Date | null | undefined) =>
    formatDateNumeric(date, i18n.language);

  const formatAmount = (amount: number, currency: string) => {
    try {
      return formatCurrency(amount, currency, i18n.language);
    } catch {
      return toHumanReadableAmount(amount);
    }
  };

  const isZero = proQty === 0;
  const hasExistingSubscription = freqData.pro.purchased > 0;

  const isDecrease = proQty < freqData.pro.purchased;
  const isIncrease = proQty > freqData.pro.purchased;

  const extraToUnassign = Math.max(0, freqData.pro.assigned - proQty);
  const needsUnassign = extraToUnassign > 0;

  // Pending invitations count against the assigned total just like accepted
  // users, so they must appear as selectable rows when reducing — otherwise
  // a manager can't satisfy the unassignment requirement on a team where some
  // seats are still pending acceptance.
  const buildAssignedList = (priceId: string | undefined): AssignedItem[] => {
    if (!priceId) return [];
    const userItems: AssignedItem[] = users
      .filter((u) => u.priceId === priceId)
      .map((u) => ({
        kind: "user",
        id: u.id,
        label: u.fullName || u.email,
        isSelf: u.id === teamUser?.id,
      }));
    const invitationItems: AssignedItem[] = invitations
      .filter((inv) => inv.status === "pending" && inv.priceId === priceId)
      .map((inv) => ({ kind: "invitation", id: inv.id, label: inv.email }));
    return [...userItems, ...invitationItems];
  };

  const assignedProItems = buildAssignedList(proPrice?.id);

  const itemKey = (item: AssignedItem) => `${item.kind}:${item.id}`;

  const selectedProCount = assignedProItems.filter((item) =>
    selectedItems.has(itemKey(item))
  ).length;

  const hasEnoughSelections = selectedProCount >= extraToUnassign;
  const remainingToSelect = Math.max(0, extraToUnassign - selectedProCount);

  const fetchPreview = useCallback(async () => {
    if (isZero || (!isDecrease && !isIncrease)) {
      setPreview(null);
      setPreviewError(false);
      return;
    }
    const seq = ++previewSeqRef.current;
    try {
      setIsLoadingPreview(true);
      setPreviewError(false);
      const { data } = await api.post<LicensePreviewDTO>(
        "/my/team/licenses/preview",
        { frequency, proQuantity: proQty }
      );
      // Drop a stale response that resolved after a newer request started.
      if (seq !== previewSeqRef.current) return;
      setPreview(data);
    } catch {
      if (seq !== previewSeqRef.current) return;
      setPreview(null);
      setPreviewError(true);
    } finally {
      if (seq === previewSeqRef.current) setIsLoadingPreview(false);
    }
  }, [frequency, proQty, isZero, isDecrease, isIncrease]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchPreview();
    }, 600);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [fetchPreview]);

  const toggleItem = (item: AssignedItem) => {
    const key = itemKey(item);
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Single-pick variant for the "only one seat to remove" case (radio UX):
  // replaces any prior pick from the same type pool with the new one.
  const selectSingleItemForType = (
    pickedKey: string,
    typeItems: AssignedItem[]
  ) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      for (const item of typeItems) next.delete(itemKey(item));
      next.add(pickedKey);
      return next;
    });
  };

  const handleConfirm = async () => {
    if (isZero) {
      onRequestCancel();
      return;
    }

    if (needsUnassign && !hasEnoughSelections) {
      toast({
        variant: "destructive",
        title: t("team.licenseManagement.dialog.selectMore", {
          count: remainingToSelect,
        }),
      });
      return;
    }

    const selectedTyped = assignedProItems.filter((item) =>
      selectedItems.has(itemKey(item))
    );
    const usersToUnassign = selectedTyped
      .filter((item) => item.kind === "user")
      .map((item) => item.id);
    const invitationsToCancel = selectedTyped
      .filter((item) => item.kind === "invitation")
      .map((item) => item.id);

    try {
      setIsSubmitting(true);
      const { data } = await api.post<LicenseQuantitiesUpdateResultDTO>(
        "/my/team/licenses/update",
        {
          frequency,
          proQuantity: proQty,
          ...(usersToUnassign.length ? { usersToUnassign } : {}),
          ...(invitationsToCancel.length ? { invitationsToCancel } : {}),
        },
        { headers: { "Idempotency-Key": idempotencyKey } }
      );
      // Quantity changes can flip a user's scheduledUnassignAt — invalidate
      // the cached users list so the team users page shows the fresh state
      // without a hard refresh.
      await apiFetchUsers({ force: true });
      toast({
        variant: "default",
        ...buildLicenseUpdateToast({
          total: proQty,
          purchased: freqData.pro.purchased,
          preview,
          currentPeriodEndsAt: freqData.currentPeriodEndsAt,
          issuedInvoice: data.issuedInvoice,
          paidByInvoice,
          t,
          formatAmount,
          formatDate,
        }),
      });
      onConfirm(data);
    } catch (error) {
      showLicenseError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openPortal = async () => {
    try {
      const { data } = await api.get<TeamBillingPortalDTO>(
        "/my/team/billing/portal"
      );
      if (data?.url) window.open(data.url, "_blank")?.focus();
    } catch {
      toastError();
    }
  };

  const title =
    frequency === "monthly"
      ? t("team.licenseManagement.dialog.titleMonthly")
      : t("team.licenseManagement.dialog.titleAnnual");

  const perPeriod =
    frequency === "monthly"
      ? t("team.licenseManagement.dialog.perMonth")
      : t("team.licenseManagement.dialog.perYear");

  const confirmLabel = isDecrease
    ? t("team.licenseManagement.dialog.confirmReduction")
    : t("team.licenseManagement.dialog.confirmPurchase");

  // Prefer the preview's discount (authoritative for this transaction), falling
  // back to the subscription-level values from the inventory.
  const discountPercent = preview?.percentOff ?? freqData.percentOff ?? null;
  const discountCode = preview?.promotionCode ?? freqData.promotionCode ?? null;
  const hasDiscount = !!discountCode || !!discountPercent;

  // A coupon applied without a promotion code has a percent but no code, and a
  // fixed-amount coupon has a code but no percent — hence three label shapes.
  const discountLabel = (() => {
    if (discountCode && discountPercent) {
      return t("team.licenseManagement.dialog.discount", {
        code: discountCode,
        percent: discountPercent,
      });
    }
    if (discountCode) {
      return t("team.licenseManagement.dialog.discountCodeOnly", {
        code: discountCode,
      });
    }
    return t("team.licenseManagement.dialog.discountPercent", {
      percent: discountPercent,
    });
  })();

  // Full (non-prorated, pre-discount) recurring price of the added seats, so the
  // user can compare it against the prorated amount charged today.
  const baseIncreaseAmount =
    Math.max(0, proQty - freqData.pro.purchased) * (proPrice?.amount ?? 0);


  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-[520px] w-full">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <Separator />

        <div className="space-y-5 overflow-auto h-[min(440px,65vh)] px-1">
          {/* Selectors */}
          {proPrice && (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">{proPrice.productName}</p>
                <p className="text-xs text-muted-foreground">
                  {toHumanReadableAmount(proPrice.amount)}
                  {perPeriod}
                </p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <LicenseCountSelector
                  value={proQty}
                  min={0}
                  onValueChange={setProQty}
                  disabled={isSubmitting}
                />
                <span className="text-xs text-muted-foreground">
                  {t("team.licenseManagement.dialog.totalAfterChange")}
                </span>
              </div>
            </div>
          )}

          {proPrice && (
            <LicenseChangeSummary
              current={freqData.pro.purchased}
              target={proQty}
              unitAmount={proPrice.amount}
              percentOff={discountPercent}
              currency={preview?.currency ?? DEFAULT_CURRENCY}
              perPeriod={perPeriod}
            />
          )}

          {/* The increase is invoiced, not charged (#742): an exempted team
              without a payment method, or a contract switched to transfer by
              hand, whose card on file changes nothing */}
          {isIncrease && paidByInvoice && (
            <Alert>
              <InfoIcon className="w-4 h-4" />
              <AlertTitle>
                {t("team.licenseManagement.dialog.invoiceTitle")}
              </AlertTitle>
              <AlertDescription>
                {hasPaymentMethod ? (
                  t("team.licenseManagement.dialog.invoiceDescriptionWithCard")
                ) : (
                  <>
                    {t("team.licenseManagement.dialog.invoiceDescription", {
                      days: INVOICE_DAYS_UNTIL_DUE,
                    })}{" "}
                    <Button
                      variant="link"
                      className="h-auto p-0 font-medium"
                      onClick={openPortal}
                    >
                      {t("team.licenseManagement.dialog.addPaymentMethod")}
                    </Button>
                  </>
                )}
              </AlertDescription>
            </Alert>
          )}

          {/* No payment method on a team that must hold one */}
          {isIncrease && !hasPaymentMethod && !paidByInvoice && (
            <Alert variant="warning">
              <AlertCircleIcon className="w-4 h-4" />
              <AlertTitle>
                {t("team.licenseManagement.dialog.noPaymentMethodTitle")}
              </AlertTitle>
              <AlertDescription>
                {t("team.licenseManagement.dialog.noPaymentMethodDescription")}{" "}
                <Button
                  variant="link"
                  className="h-auto p-0 font-medium"
                  onClick={openPortal}
                >
                  {t("team.licenseManagement.dialog.addPaymentMethod")}
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {isIncrease && hasPaymentMethod && !paidByInvoice && (
            <Alert>
              <InfoIcon className="w-4 h-4" />
              <AlertDescription>
                {t("team.licenseManagement.dialog.increaseInfo")}
              </AlertDescription>
            </Alert>
          )}

          {/* Users to unassign — split per license type since the API
              enforces the minimum per type */}
          {needsUnassign && (
            <div className="space-y-2">
              <Alert variant="warning">
                <AlertCircleIcon className="w-4 h-4" />
                <AlertTitle>
                  {t("team.licenseManagement.dialog.licensesToRemove", {
                    count: extraToUnassign,
                  })}
                </AlertTitle>
                <AlertDescription>
                  {t("team.licenseManagement.dialog.usersWillLoseAccess")}
                </AlertDescription>
              </Alert>
              <p className="text-sm font-medium">
                {t("team.licenseManagement.dialog.selectUsersToRemove")}
              </p>

              {extraToUnassign > 0 && proPrice && (
                <AssignedItemPicker
                  productName={proPrice.productName}
                  items={assignedProItems}
                  selectedItems={selectedItems}
                  itemKey={itemKey}
                  required={extraToUnassign}
                  selectedCount={selectedProCount}
                  onToggle={toggleItem}
                  onSelectSingle={selectSingleItemForType}
                  pendingLabel={t(
                    "team.licenseManagement.dialog.pendingInvitation"
                  )}
                  ownSeatLabel={t("team.licenseManagement.dialog.ownSeat")}
                />
              )}

              {remainingToSelect > 0 && (
                <p className="text-xs text-destructive">
                  {t("team.licenseManagement.dialog.selectMore", {
                    count: remainingToSelect,
                  })}
                </p>
              )}
            </div>
          )}

          {/* Preview section */}
          {isLoadingPreview && (
            <div className="flex justify-center py-2">
              <Loader2Icon className="w-4 h-4 animate-spin text-muted-foreground" />
            </div>
          )}

          {previewError && !isLoadingPreview && !isZero && (
            <Alert variant="destructive">
              <AlertCircleIcon className="w-4 h-4" />
              <AlertTitle>
                {t("team.licenseManagement.dialog.previewErrorTitle")}
              </AlertTitle>
              <AlertDescription className="flex items-center gap-2">
                {t("team.licenseManagement.dialog.previewErrorBody")}
                <Button variant="outline" size="sm" onClick={fetchPreview}>
                  {t("team.licenseManagement.retry")}
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {preview && !isLoadingPreview && !isZero && (
            <div className="space-y-2 rounded-md border p-3 text-sm">
              {preview.isScheduledChange ? (
                <>
                  <Alert variant="warning">
                    <AlertCircleIcon className="w-4 h-4" />
                    <AlertTitle>
                      {t("team.licenseManagement.dialog.decreaseWarningTitle", {
                        date: formatDate(preview.scheduledEffectiveAt),
                      })}
                    </AlertTitle>
                    <AlertDescription>
                      {t("team.licenseManagement.dialog.decreaseWarningBody")}
                    </AlertDescription>
                  </Alert>
                </>
              ) : (
                <>
                  {preview.prorationDays !== null && preview.prorationStartDate && preview.prorationEndDate && (
                    <p className="text-xs text-muted-foreground">
                      {t("team.licenseManagement.dialog.prorationPeriod", {
                        days: preview.prorationDays,
                        startDate: formatDate(preview.prorationStartDate),
                        endDate: formatDate(preview.prorationEndDate),
                      })}
                    </p>
                  )}

                  {baseIncreaseAmount > 0 && (
                    <div className="flex justify-between text-muted-foreground">
                      <span>{t("team.licenseManagement.dialog.basePrice")}</span>
                      <span>
                        {formatAmount(baseIncreaseAmount, preview.currency)}
                        {perPeriod}
                      </span>
                    </div>
                  )}

                  {/* The promotion is surfaced whenever Stripe holds one, even
                      when the invoice preview reports no discount line — the
                      customer still needs to see which code is in play. */}
                  {hasDiscount && (
                    <>
                      {preview.immediateDiscountAmount > 0 && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            {t("team.licenseManagement.dialog.subtotalHT")}
                          </span>
                          <span>
                            {formatAmount(
                              preview.immediateChargeExclTax +
                                preview.immediateDiscountAmount,
                              preview.currency
                            )}
                          </span>
                        </div>
                      )}

                      <div className="flex justify-between text-green-600 dark:text-green-400">
                        <span>{discountLabel}</span>
                        <span>
                          {preview.immediateDiscountAmount > 0
                            ? `-${formatAmount(
                                preview.immediateDiscountAmount,
                                preview.currency
                              )}`
                            : discountPercent
                              ? `-${discountPercent}%`
                              : ""}
                        </span>
                      </div>
                    </>
                  )}

                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {t(
                        paidByInvoice
                          ? "team.licenseManagement.dialog.invoicedHT"
                          : "team.licenseManagement.dialog.immediateChargeHT"
                      )}
                    </span>
                    <span className="font-medium">
                      {formatAmount(
                        preview.immediateChargeExclTax,
                        preview.currency
                      )}
                    </span>
                  </div>

                  {preview.immediateTax > 0 && (
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>{t("team.licenseManagement.dialog.tax")}</span>
                      <span>
                        {formatAmount(preview.immediateTax, preview.currency)}
                      </span>
                    </div>
                  )}

                  <div className="flex justify-between border-t pt-2 font-semibold">
                    <span>
                      {t(
                        paidByInvoice
                          ? "team.licenseManagement.dialog.invoicedTTC"
                          : "team.licenseManagement.dialog.immediateChargeTTC"
                      )}
                    </span>
                    <span>
                      {formatAmount(
                        preview.immediateChargeInclTax,
                        preview.currency
                      )}
                    </span>
                  </div>

                  <Separator />

                  <div className="flex justify-between text-muted-foreground">
                    <span>
                      {t("team.licenseManagement.dialog.nextAmountHT")}
                    </span>
                    {discountPercent ? (
                      <span className="flex items-baseline gap-2">
                        <span className="line-through">
                          {formatAmount(
                            preview.nextRenewalExclTax,
                            preview.currency
                          )}
                        </span>
                        <span className="font-medium text-foreground">
                          {formatAmount(
                            preview.nextRenewalExclTax *
                              (1 - discountPercent / 100),
                            preview.currency
                          )}
                        </span>
                      </span>
                    ) : (
                      <span>
                        {formatAmount(
                          preview.nextRenewalExclTax,
                          preview.currency
                        )}
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {isDecrease && !preview?.isScheduledChange && (
            <Alert>
              <InfoIcon className="w-4 h-4" />
              <AlertDescription>
                {t("team.licenseManagement.dialog.licensesActiveUntilRenewal")}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <Separator />

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
            {t("team.licenseManagement.dialog.cancel")}
          </Button>
          {isZero && hasExistingSubscription ? (
            <Button variant="destructive" onClick={onRequestCancel}>
              {t("team.licenseManagement.dialog.cancelSubscription")}
            </Button>
          ) : (
            <ButtonLoading
              isLoading={isSubmitting}
              disabled={
                isSubmitting ||
                isZero ||
                isLoadingPreview ||
                previewError ||
                (needsUnassign && !hasEnoughSelections) ||
                (isIncrease &&
                  !hasPaymentMethod &&
                  !paidByInvoice)
              }
              onClick={handleConfirm}
            >
              {confirmLabel}
            </ButtonLoading>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

type AssignedItemPickerProps = {
  productName: string;
  items: AssignedItem[];
  selectedItems: Set<string>;
  itemKey: (item: AssignedItem) => string;
  required: number;
  selectedCount: number;
  onToggle: (item: AssignedItem) => void;
  onSelectSingle: (pickedKey: string, typeItems: AssignedItem[]) => void;
  pendingLabel: string;
  ownSeatLabel: string;
}

/**
 * The manager's own row is flagged: giving up their own seat is allowed (they
 * keep billing access without a license), but it should be a deliberate pick
 * rather than a row they checked without noticing it was theirs.
 */
const ItemLabel = ({
  item,
  pendingLabel,
  ownSeatLabel,
}: {
  item: AssignedItem;
  pendingLabel: string;
  ownSeatLabel: string;
}) => (
  <>
    <span className="text-sm">{item.label}</span>
    {item.kind === "invitation" && (
      <Badge variant="warning" className="ml-auto">
        {pendingLabel}
      </Badge>
    )}
    {item.kind === "user" && item.isSelf && (
      <Badge variant="outline" className="ml-auto">
        {ownSeatLabel}
      </Badge>
    )}
  </>
);

const AssignedItemPicker = ({
  productName,
  items,
  selectedItems,
  itemKey,
  required,
  selectedCount,
  onToggle,
  onSelectSingle,
  pendingLabel,
  ownSeatLabel,
}: AssignedItemPickerProps) => {
  const isSingleSelect = required === 1;

  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-muted-foreground">
        {productName} ({selectedCount}/{required})
      </p>
      <div className="space-y-2 border rounded-md p-3 max-h-[140px] overflow-y-auto">
        {isSingleSelect ? (
          <RadioGroup
            value={
              items.find((item) => selectedItems.has(itemKey(item)))
                ? itemKey(
                    items.find((item) => selectedItems.has(itemKey(item)))!
                  )
                : ""
            }
            onValueChange={(key) => onSelectSingle(key, items)}
          >
            {items.map((item) => {
              const key = itemKey(item);
              return (
                <label
                  key={key}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <RadioGroupItem value={key} />
                  <ItemLabel
                    item={item}
                    pendingLabel={pendingLabel}
                    ownSeatLabel={ownSeatLabel}
                  />
                </label>
              );
            })}
          </RadioGroup>
        ) : (
          items.map((item) => {
            const key = itemKey(item);
            const isChecked = selectedItems.has(key);
            return (
              <label
                key={key}
                className="flex items-center gap-2 cursor-pointer"
              >
                <Checkbox
                  checked={isChecked}
                  onCheckedChange={() => onToggle(item)}
                  disabled={!isChecked && selectedCount >= required}
                />
                <ItemLabel
                  item={item}
                  pendingLabel={pendingLabel}
                  ownSeatLabel={ownSeatLabel}
                />
              </label>
            );
          })
        )}
      </div>
    </div>
  );
};
